import React from 'react';
import { useDispatch } from 'react-redux';
import { type AxiosError } from 'axios';
import { LinearProgress, Paper, styled, Typography } from '@mui/material';
import { type DatesSetArg } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin from '@fullcalendar/interaction';
import { useDeepCompareEffect } from '@fuse/hooks';

import { type MiddlewhereFilter } from 'app/main/components/filter/FilterItemManager';
import { executeStored, today, type ApiError } from 'app/main/components/Utils';
import useSettings from 'app/main/components/hooks/useSettings';
import useUserData from 'app/main/components/hooks/useUserData';
import usePagination from 'app/main/components/hooks/usePagination';
import ViewCalendarToolbar from './components/ViewCalendarToolbar';
import locales from './components/ViewCalendarLocales';

interface ViewCalendarProps {
	id?: string;
	module: string;
	itemModel: {
		id: string;
		title: string | ((model: any) => string);
		start: string;
		end: string;
		color: string | ((model: any) => string);
	};
	storedProcedure?: string;
	defaultFilters?: MiddlewhereFilter[];
	filters?: MiddlewhereFilter[];
	defaultQuery?: any;
	query?: any;
	handleEventClick;
	refresh?: boolean;
	updateRefresh?: VoidFunction;
	handleDateClick?: (args: any) => void;
}

const Root = styled(Paper)(({ theme }) => ({
	'& a': {
		color: `${theme.palette.text.primary} !important`,
		textDecoration: 'none !important'
	},
	'&  .fc-media-screen': {
		// minHeight: '100%',
		// scroll: 'auto'
	},
	'& .fc-scrollgrid, & .fc-theme-standard td, & .fc-theme-standard th': {
		borderColor: `${theme.palette.divider} !important`
	},
	'&  .fc-scrollgrid-section > td': {
		border: 0
	},
	'& .fc-daygrid-day': {
		'&:last-child': {
			borderRight: 0
		}
	},
	'& .fc-col-header-cell': {
		borderWidth: '0 0 1px 0',
		padding: '8px 0',
		'& .fc-col-header-cell-cushion': {
			color: theme.palette.text.secondary,
			fontWeight: 500
		}
	},
	'& .fc-view ': {
		// borderRadius: 20,
		// overflow: 'hidden',
		// border: `1px solid ${theme.palette.divider}`,
		'& > .fc-scrollgrid': {
			// border: 0
		}
	},
	'& .fc-daygrid-day-number': {
		color: theme.palette.text.secondary,
		fontWeight: 500
	},
	'& .fc-event': {
		border: 0,
		padding: '0 4px',
		borderRadius: '8px'
	}
}));

export default function ViewCalendar(props: ViewCalendarProps) {
	const dispatch = useDispatch();
	const { settings } = useSettings(props.module);
	const userData = useUserData();

	const calendarRef = React.useRef<FullCalendar>(null);

	const [items, setItems] = React.useState<any[]>([]);
	const [isLoading, setIsLoading] = React.useState(true);
	const [error, setError] = React.useState<ApiError>();

	const [datesRange, setDatesRange] = React.useState<DatesSetArg>();

	const { filterProps } = usePagination({
		id: props.id,
		module: props.module,
		filter: {
			defaultFilters: [
				...(props.defaultFilters || []),
				{
					filt_field: `${settings?.modu_short}_deleted`,
					filt_clause: 'not_equal',
					filt_value: 1
				},
				{
					filt_field: `${settings?.modu_short}_d_from`,
					filt_clause: 'smaller',
					filt_value: datesRange?.end
				},
				{
					filt_field: `${settings?.modu_short}_d_to`,
					filt_clause: 'greater',
					filt_value: datesRange?.start
				}
			],
			filters: props.filters
		}
	});

	useDeepCompareEffect(() => {
		const controller = new AbortController();

		setIsLoading(true);
		executeStored({
			action:
				props.storedProcedure ||
				(!settings.modu_systemtable
					? `${settings.modu_external ? 'ext_' : ''}st_${props.module}_list`
					: 'st_sys_systable_list'),
			params: {
				n_page: 1,
				n_rows: 999,
				filters: filterProps.filters,
				query: filterProps.query
			},
			dispatch,
			signal: controller.signal,
			successHandler: response => {
				const list = response.data.list?.map(model => ({
					id: model[props.itemModel.id],
					title:
						typeof props.itemModel.title === 'function'
							? props.itemModel.title(model)
							: model[props.itemModel.title],
					start: model[props.itemModel.start],
					end: model[props.itemModel.end],
					color:
						typeof props.itemModel.color === 'function'
							? props.itemModel.color(model)
							: model[props.itemModel.color],
					model
				}));
				setItems(list || []);
				setIsLoading(false);
			},
			errorHandler: ex => {
				setError(ex);
				setIsLoading(false);
			}
		});

		return () => {
			controller.abort();
		};
	}, [
		dispatch,
		filterProps.filters,
		filterProps.query,
		props.itemModel,
		props.module,
		props.refresh,
		props.query,
		props.storedProcedure,
		settings.modu_systemtable,
		settings.modu_external
	]);

	return (
		<Root
			className="relative flex h-full flex-auto flex-col rounded-8"
			variant="outlined"
			sx={{
				...(props.handleDateClick && {
					'& .fc-scrollgrid-sync-table': {
						cursor: 'pointer'
					}
				})
			}}
		>
			<ViewCalendarToolbar
				module={props.module}
				calendarRef={calendarRef}
				datesRange={datesRange}
				filterProps={filterProps}
			/>
			{isLoading && <LinearProgress variant="query" color="inherit" />}
			<FullCalendar
				ref={calendarRef}
				plugins={[dayGridPlugin, timeGridPlugin, multiMonthPlugin, interactionPlugin]}
				height="100%"
				events={items}
				eventContent={eventInfo => (
					<Typography
						className="w-full cursor-pointer whitespace-normal p-2 text-12 font-500"
						sx={theme => ({
							color: theme.palette.getContrastText(
								eventInfo.backgroundColor !== 'undefined' ? eventInfo.backgroundColor : '#FFFFFF'
							)
						})}
					>
						{eventInfo.event.title}
					</Typography>
				)}
				eventClick={eventInfo => props.handleEventClick(items.find(item => item.id === eventInfo.event.id))}
				eventDisplay="block"
				// displayEventEnd
				slotEventOverlap={false}
				expandRows
				initialDate={today().toDate()}
				initialView="dayGridMonth"
				// multiMonthMaxColumns={1}
				headerToolbar={false}
				datesSet={setDatesRange}
				locales={locales}
				locale={userData.user_language}
				dayMaxEvents={false}
				dayMaxEventRows={false}
				dateClick={args => {
					if (props.handleDateClick) {
						props.handleDateClick(args);
					}
				}}
				// weekends
			/>
		</Root>
	);
}
